import {
  BriefcaseBusiness,
  CalendarClock,
  Clock,
  ClockArrowDown,
  ClockArrowUp,
} from 'lucide-react';
import {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Link, useNavigate} from 'react-router-dom';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {IconButton} from '@/components/ui/icon-button';
import {useGroupAuthorize} from '@/hooks/useAuth';
import {useEntitledForClient} from '@/hooks/useEntitlements';
import {graphqlClient} from '@/services/graphqlClient';
import {
  useGetMyWorkTimeStatusQuery,
  useStartMyWorkTimeShiftMutation,
  WorkTimeSessionState,
} from '@/services/graphqlHooks';
import queryClient from '@/services/queryClient';
import {cn} from '@/utils/ui';

import {workTimeTrackingGroups} from '../utils/workTimeGroups';
import FinishWorkDialog from './FinishWorkDialog';
import LiveDuration from './LiveDuration';

function WorkTimeStatusDropdown() {
  const {t} = useTranslation();
  const navigate = useNavigate();
  const isWorkTimeGroup = useGroupAuthorize(workTimeTrackingGroups);
  const isWorkTimeEntitled = useEntitledForClient('WORK_TIME');
  const [isFinishWorkDialogOpen, setIsFinishWorkDialogOpen] = useState(false);
  const workTimeStatus = useGetMyWorkTimeStatusQuery(graphqlClient, undefined, {
    enabled: isWorkTimeEntitled && isWorkTimeGroup,
    refetchInterval: 60_000,
    staleTime: 30_000,
  });
  const startWork = useStartMyWorkTimeShiftMutation(graphqlClient);
  const status = workTimeStatus.data?.getMyWorkTimeStatus;
  const activeSession = status?.activeSession ?? undefined;
  const isWorking = status?.state === WorkTimeSessionState.Working;
  const isActionPending = startWork.isPending || workTimeStatus.isFetching;

  const handleStartWork = async () => {
    await startWork.mutateAsync({});
    await queryClient.invalidateQueries({
      queryKey: useGetMyWorkTimeStatusQuery.getKey(),
    });
    navigate('/');
  };

  const handleFinishWork = () => {
    setIsFinishWorkDialogOpen(true);
  };

  if (!isWorkTimeEntitled || !isWorkTimeGroup) return null;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <IconButton
            type="button"
            variant="tertiary"
            size="default"
            className="relative shrink-0 bg-white shadow-subtle"
            aria-label={
              isWorking ? t('Work is started') : t('Work is not started')
            }
          >
            <BriefcaseBusiness />
            <span
              className={cn(
                'absolute right-1.5 bottom-1.5 size-2.5 rounded-full ring-2 ring-white',
                isWorking ? 'bg-accent-700' : 'bg-danger-800'
              )}
            />
          </IconButton>
        </DropdownMenuTrigger>
        <DropdownMenuContent side="bottom" align="end" className="min-w-56">
          <DropdownMenuLabel>
            {isWorking ? t('Work is started') : t('Work is not started')}
          </DropdownMenuLabel>
          {isWorking && (
            <div className="flex items-center gap-2 px-2 pb-1 text-muted-foreground">
              <Clock className="size-4 shrink-0" />
              <span className="font-mono text-sm tabular-nums">
                <LiveDuration startedAt={activeSession?.startedAt} />
              </span>
            </div>
          )}
          {!isWorking && (
            <div className="flex items-center gap-2 px-2 pb-1 text-sm text-muted-foreground">
              <BriefcaseBusiness className="size-4 shrink-0" />
              <span className="text-sm">{t('Not started')}</span>
            </div>
          )}
          <DropdownMenuSeparator />
          {isWorking ? (
            <DropdownMenuItem
              disabled={isActionPending}
              onClick={handleFinishWork}
            >
              <ClockArrowDown className="size-4" />
              {t('Finish work')}
            </DropdownMenuItem>
          ) : (
            <DropdownMenuItem
              disabled={isActionPending}
              onClick={handleStartWork}
            >
              <ClockArrowUp className="size-4" />
              {t('Start work')}
            </DropdownMenuItem>
          )}
          <DropdownMenuItem asChild>
            <Link className="flex items-center gap-2" to="/work-time/history">
              <CalendarClock className="size-4" />
              {t('Check my work time')}
            </Link>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <FinishWorkDialog
        open={isFinishWorkDialogOpen}
        onOpenChange={setIsFinishWorkDialogOpen}
      />
    </>
  );
}

export default WorkTimeStatusDropdown;
